import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, AlertCircle } from "lucide-react";
import { addressError, SAMPLE_TOKENS } from "../../lib/trace";

export const TokenSearch = ({ initial = "", size = "lg", showSamples = true }) => {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const big = size === "lg";

  const submit = (e) => {
    e.preventDefault();
    const addr = value.trim();
    const err = addressError(addr);
    setError(err);
    if (!err) navigate(`/scan/${addr}`);
  };

  return (
    <div className="w-full">
      <form onSubmit={submit} role="search" aria-label="Analyse a Solana token" noValidate
        className={`flex flex-col gap-2 rounded-2xl border bg-trace-panel/90 p-2 backdrop-blur-xl transition-colors duration-200 sm:flex-row ${error ? "border-red-500/60" : "border-trace-line focus-within:border-trace-green/50"}`}>
        <label htmlFor={`token-input-${size}`} className="sr-only">Solana token mint address</label>
        <div className="flex flex-1 items-center gap-3 px-3">
          <Search size={18} className="shrink-0 text-trace-dim" aria-hidden="true" />
          <input
            id={`token-input-${size}`}
            data-testid="token-search-input"
            value={value}
            onChange={(e) => { setValue(e.target.value); if (error) setError(null); }}
            placeholder="Paste a Solana token mint address"
            spellCheck={false}
            autoComplete="off"
            aria-invalid={!!error}
            aria-describedby={error ? "token-search-error" : undefined}
            className={`w-full min-w-0 bg-transparent font-mono text-white placeholder:font-sans placeholder:text-trace-dim focus:outline-none ${big ? "py-3 text-sm sm:text-base" : "py-2 text-sm"}`}
          />
        </div>
        <button
          type="submit"
          data-testid="analyse-token-button"
          className={`group inline-flex items-center justify-center gap-2 rounded-xl bg-trace-green font-semibold text-trace-bg transition-[transform,box-shadow] duration-200 hover:shadow-[0_0_24px_rgba(0,255,102,0.35)] active:scale-[0.98] ${big ? "px-6 py-3.5" : "px-5 py-2.5 text-sm"}`}
        >
          Analyse Token <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
      </form>
      {error && (
        <p id="token-search-error" role="alert" data-testid="token-search-error" className="mt-2 flex items-center gap-1.5 text-sm text-red-400">
          <AlertCircle size={14} /> {error}
        </p>
      )}
      {showSamples && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="eyebrow mr-1">Try</span>
          {SAMPLE_TOKENS.map((t) => (
            <button key={t.label} type="button" data-testid={`sample-token-${t.label.toLowerCase()}`}
              onClick={() => navigate(`/scan/${t.address}`)}
              className="rounded-full border border-trace-line bg-trace-card px-3 py-1 font-mono text-xs text-trace-muted transition-colors duration-200 hover:border-trace-green/40 hover:text-trace-green">
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
